import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SewTab } from './SewTab';
import { toast } from 'sonner';
import { X, Play, Mountain, Loader2, Bot, Square, RotateCcw, FileText, BookOpen } from 'lucide-react';
import { openHelp } from './HelpBtn';
import { simulate, cancelSim } from '../lib/aquaNet';
import { AquaQuality } from './AquaQuality';
import { SourcePick } from './AquaSourcePick';
import { setAqua, useAqua, aquaState } from '../lib/aquaStore';
import { AquaStep } from './AquaStep';
import { AquaTemp } from './AquaTemp';
import { AquaCal } from './AquaCal';
import { AquaLab } from './AquaLab';
import { AquaAge } from './AquaAge';
import { AquaLegend } from './AquaLegend';
import { AquaRef } from './AquaRef';
import { AquaDemo } from './AquaDemo';
import { AquaLeaks } from './AquaLeaks';
import { aquaPdf } from '../lib/aquaPdf';
import useRiverDyke from '../hooks/useRiverDyke';
import { useMode, applyProfiles } from '../lib/profiles';
import { ImportGaps } from './ImportGaps';
import { gapsOf } from '../lib/importGaps';
import { NI } from '../lib/i18nNetImport';

export const fmtQ = (cfg, v) => (cfg.qUnit === 'm3h' ? `${n1(v * 3.6, 2)} m³/h` : `${n1(v, 2)} l/s`);
const n1 = (v, d = 1) => (v == null || !Number.isFinite(v) ? '–' : v.toFixed(d).replace('.', ','));
const nm = (t, f) => f?.name || `${t['fit_' + f?.type] || f?.type} ${String(f?.id || '').slice(-4)}`;

function Valves({ t, project, updateProject, cfg, setCfg }) {
  const vs = (project.fittings || []).filter((f) => f.type === 'valve' || f.type === 'prv');
  const setF = (id, p) => updateProject((pr) => ({ ...pr, fittings: pr.fittings.map((f) => (f.id === id ? { ...f, ...p } : f)) }));
  const sv = cfg.scen?.valves || {}, pos = (f) => sv[f.id] || f.state;
  const setPos = (f, v) => { const n = { ...sv }; if (v === (f.state || 'unknown')) delete n[f.id]; else n[f.id] = v; setCfg({ scen: { ...(cfg.scen || {}), valves: n } }); };
  if (!vs.length) return <p className="muted" data-testid="aq-no-valves">{t.aqNoValves}</p>;
  return (
    <div className="aq-valves" data-testid="aq-valve-list">
      {vs.map((f) => (
        <div key={f.id} className="aq-row">
          <span>{f.type === 'prv' ? '⬇️' : '🔧'} {nm(t, f)}</span>
          {f.type === 'prv'
            ? <label>{t.aqSet}<input type="number" step="0.1" data-testid={`aq-prv-set-${f.id}`} value={f.hyd?.set ?? 3} onChange={(e) => setF(f.id, { hyd: { ...(f.hyd || {}), set: e.target.value } })} /> bar</label>
            : <>
              <small className="aq-real" data-testid={`aq-valve-real-${f.id}`}>{t.aqReal}: {t['aqPos_' + (['open', 'closed', 'partial'].includes(f.state) ? f.state : 'unknown')]}</small>
              <button className={`chip ${pos(f) === 'closed' ? 'aq-closed' : 'aq-open'} ${sv[f.id] ? 'aq-scen' : ''}`} data-testid={`aq-valve-toggle-${f.id}`} onClick={() => setPos(f, pos(f) === 'closed' ? 'open' : 'closed')}>{pos(f) === 'closed' ? t.aqClosed : pos(f) === 'open' || pos(f) === 'partial' ? t.aqOpen : `${t.aqOpen}?`}</button>
            </>}
        </div>
      ))}
      {Object.keys(sv).length > 0 && <p className="aq-scen-note" data-testid="aq-scen-note">✎ {t.aqScenNote.replace('{n}', Object.keys(sv).length)} <button className="chip" data-testid="aq-scen-reset" onClick={() => setCfg({ scen: { ...(cfg.scen || {}), valves: {} } })}><RotateCcw size={14} /> {t.aqScenReset}</button></p>}
    </div>
  );
}

function Draws({ t, res, byId, cfg, setCfg, updateProject }) {
  if (!res.draws?.length) return null;
  const off = (d) => (d.fire ? setCfg({ fire: { ...(cfg.fire || {}), id: '' } }) : updateProject((p) => ({ ...p, fittings: p.fittings.map((f) => (f.id === d.fid ? { ...f, hyd: { ...(f.hyd || {}), hOpen: false } } : f)) })));
  return (
    <div className="aq-draws" data-testid="aq-draws"><b>🚒 {t.aqDraws}</b>
      {res.draws.map((d) => <div key={d.fid + d.fire} className="aq-row" data-testid={`aq-draw-${d.fid}`}>
        <span>{nm(t, byId[d.fid])}: {d.fire ? t.aqFireTag : t.aqOpenTag} {n1(d.want, 2)} l/s → {t.aqGot} <b>{n1(d.got, 2)} l/s</b>{d.got < d.want * 0.95 ? ` · ⚠️ ${res.noWater?.some((x) => x.fid === d.fid) ? t.aqNoWaterShort : t.aqTooLow}` : ''}</span>
        <button className="chip" data-testid={`aq-draw-off-${d.fid}`} onClick={() => off(d)}>{t.aqDrawOff}</button></div>)}
      <small className="muted">{t.aqDrawHint}</small>
    </div>
  );
}

function PumpChk({ t, res, byId }) {
  const c = res.pumpChk;
  if (!c) return null;
  return (
    <div className="aq-err" data-testid="aq-pump-chk"><b>⚠️ {t.aqPumpChk.replace('{h}', nm(t, byId[c.fid])).replace('{z}', n1(c.z, 1)).replace('{p}', n1(c.stat, 2)).replace('{m}', n1(c.need, 1)).replace('{b}', n1(c.need / 10.197, 2))}</b>
      {c.srcs.map((s) => <p key={s.fid}>{nm(t, byId[s.fid])}: {s.tank ? t.aqPumpTank.replace('{a}', n1(s.now, 1)).replace('{b}', n1(s.want, 1)) + (s.top != null && s.want > s.top ? t.aqPumpTankMax.replace('{c}', n1(s.top, 1)) : '') : t.aqPumpSrc.replace('{a}', n1(s.now, 2)).replace('{b}', n1(s.want, 2))}</p>)}
      {c.prv?.map((p) => <p key={p.fid} data-testid="aq-pump-chk-prv">{nm(t, byId[p.fid])}: {t.aqPumpPrv.replace('{a}', n1(p.set, 1)).replace('{b}', n1(p.want, 1))}</p>)}
      {c.pumps.map((p) => <p key={p.fid}>{nm(t, byId[p.fid])}: {t.aqPumpPump.replace('{a}', n1(p.gain, 1)).replace('{b}', n1(p.want, 1))}</p>)}
    </div>
  );
}

function Result({ t, res, project, cfg, setCfg, updateProject }) {
  if (!res) return null;
  if (res.error === 'noSource') return <SourcePick t={t} project={project} updateProject={updateProject} />;
  const byId = Object.fromEntries((project.fittings || []).map((f) => [f.id, f])), pipes = Object.fromEntries((project.pipes || []).map((p) => [p.id, p]));
  const names = (id) => (byId[id] ? nm(t, byId[id]) : pipes[id]?.name || String(id).slice(-6));
  if (res.error) return <><p className="aq-err" data-testid="aq-error">{t['aqErr_' + res.error] || t.aqErr_calc}{res.detail ? ` (${res.detail})` : ''}</p><AquaQuality t={t} q={res.quality} names={names} /></>;
  return (
    <div className="aq-res" data-testid="aq-result">
      {res.key !== JSON.stringify([cfg.scen?.valves || {}, cfg.demandF, cfg.fire]) && <p className="aq-warn" data-testid="aq-outdated">{t.aqOutdated}</p>}
      <p className="aq-status" data-testid="aq-status">{res.scenario ? t.aqStatScen : t.aqStatBase} · {new Date(res.at).toLocaleString(t.locale)}</p>
      <div className="aq-kpi"><b data-testid="aq-inflow">{fmtQ(cfg, res.inflow)}</b><small>{t.aqInflow}</small></div>
      <div className="aq-kpi"><b data-testid="aq-pmin">{n1(res.min)} – {n1(res.max)} bar</b><small>{t.aqPrange}</small></div>
      {res.pumps?.map((u) => <div key={u.fid} className="aq-kpi" data-testid={`aq-pump-res-${u.fid}`}><b>{u.on ? `${fmtQ(cfg, u.q)} · +${n1(u.gain)} m` : t.aqPumpOff}</b><small>⚙️ {nm(t, byId[u.fid])}</small></div>)}
      {res.min != null && res.min < 1.5 && <p className="aq-warn" data-testid="aq-low-warn">{t.aqLow}</p>}
      {res.max > 8 && <p className="aq-warn" data-testid="aq-high-warn">{t.aqHigh}</p>}
      {res.leakTest && <p className="aq-note" data-testid="aq-leaktest-note">💧 {res.leakTest === 'only' ? t.aqLeakOnlyNote : t.aqLeakNetNote}</p>}
      <PumpChk t={t} res={res} byId={byId} />
      <Draws t={t} res={res} byId={byId} cfg={cfg} setCfg={setCfg} updateProject={updateProject} />
      {res.short?.length > 0 && <div className="aq-dry" data-testid="aq-short"><b>{t.aqShort} ({res.short.length})</b><p>{res.short.slice(0, 30).map((x) => `${nm(t, byId[x.fid])} (${n1(x.got, 2)}/${n1(x.want, 2)} l/s)`).join(', ')}</p></div>}
      {res.noWater?.length > 0 && <div className="aq-dry" data-testid="aq-nowater"><b>⛔ {t.aqNoWaterList} ({res.noWater.length})</b><p>{res.noWater.map((x) => `${nm(t, byId[x.fid])} (+${n1(x.above, 1)} m)`).join(', ')}</p></div>}
      {res.unsupplied.length > 0 && (
        <div className="aq-dry" data-testid="aq-unsupplied"><b>{t.aqDry} ({res.unsupplied.length})</b>
          <p>{res.unsupplied.slice(0, 30).map((id) => nm(t, byId[id])).join(', ')}</p></div>
      )}
      <AquaQuality t={t} q={res.quality} names={names} />
      <div className="row" data-testid="aq-report">
        <button className="btn btn-dark btn-sm" data-testid="aq-pdf-full-btn" onClick={() => aquaPdf({ t, project, res, cfg, full: true }).catch((e) => toast.error(String(e?.message || e)))}><FileText size={14} /> {t.aqRepFull}</button>
        <button className="btn btn-outline btn-sm" data-testid="aq-pdf-short-btn" onClick={() => aquaPdf({ t, project, res, cfg, full: false }).catch((e) => toast.error(String(e?.message || e)))}><FileText size={14} /> {t.aqRepShortBtn}</button>
      </div>
      <p className="aq-disc" data-testid="aq-disclaimer">{t.aqDisc}</p>
    </div>
  );
}

function SimTab({ t, project, updateProject, cfg, setCfg }) {
  const s = useAqua();
  const [busy, setBusy] = useState(false);
  const hyd = (project.fittings || []).filter((f) => f.type === 'hydrant');
  const gaps = gapsOf(project).n;
  const run = async () => {
    if (gaps) { toast.warning((NI[document.documentElement.lang] || NI.en).gapBlock); return; }
    setBusy(true);
    try { const res = await simulate(project, project.aqua || cfg); setAqua({ res, th: null, on: !res.error, mode: s.mode === 't' || s.mode === 'age' ? 'p' : s.mode }); } catch (e) { toast.error(String(e?.message || e)); }
    setBusy(false);
  };
  // leak test: all hydrant withdrawals and fire flow off (stored), optionally all consumers off for this run
  const leakRun = async (only) => {
    const off = (fs) => fs.map((f) => (f.type === 'hydrant' && f.hyd?.hOpen ? { ...f, hyd: { ...f.hyd, hOpen: false } } : f));
    const p2 = { ...project, fittings: off(project.fittings), aqua: { ...(project.aqua || cfg), fire: { ...(cfg.fire || {}), id: '' } } };
    if (!p2.fittings.some((f) => f.type === 'leak' && f.hyd?.leakOn)) toast.warning(t.aqLeakNoneOn);
    updateProject((p) => ({ ...p, fittings: off(p.fittings), aqua: { ...(p.aqua || {}), fire: { ...(p.aqua?.fire || {}), id: '' } } }));
    setBusy(true);
    try { const res = await simulate(p2, p2.aqua, { leakOnly: only, leakTest: only ? 'only' : 'net' }); setAqua({ res, th: null, on: !res.error, mode: s.mode === 't' || s.mode === 'age' ? 'p' : s.mode }); } catch (e) { toast.error(String(e?.message || e)); }
    setBusy(false);
  };  // one-click changes in the object sheet recalculate an existing result
  useEffect(() => { const f = () => { if (aquaState().res && !busy) run(); }; window.addEventListener('giss-aqua-rerun', f); return () => window.removeEventListener('giss-aqua-rerun', f); });

  return (
    <>
      <p className="muted">{t.aqSimHint}</p>
      <div className="aq-grid">
        <label>{t.aqDemandF}<input type="number" step="0.1" min="0" data-testid="aq-demand-factor" value={cfg.demandF ?? 1} onChange={(e) => setCfg({ demandF: e.target.value })} /></label>
        <label>{t.aqFire}<select data-testid="aq-fire-select" value={cfg.fire?.id || ''} onChange={(e) => setCfg({ fire: { ...(cfg.fire || {}), id: e.target.value, q: cfg.fire?.q ?? 13.3 } })}><option value="">–</option>{hyd.map((f) => <option key={f.id} value={f.id}>{nm(t, f)}</option>)}</select></label>
        <label>{t.aqQUnit}<select data-testid="aq-q-unit" value={cfg.qUnit || 'ls'} onChange={(e) => setCfg({ qUnit: e.target.value })}><option value="ls">l/s</option><option value="m3h">m³/h</option></select></label>
        {cfg.fire?.id && <label>{t.aqFireQ}<input type="number" step="0.1" data-testid="aq-fire-q" value={cfg.fire.q} onChange={(e) => setCfg({ fire: { ...cfg.fire, q: e.target.value } })} /></label>}
      </div>
      <h4>{t.aqValves}</h4>
      <Valves t={t} project={project} updateProject={updateProject} cfg={cfg} setCfg={setCfg} />
      <ImportGaps project={project} updateProject={updateProject} />
      <div className="aq-row">
        <button className="btn btn-accent aq-run" data-testid="aq-run-btn" disabled={busy || !!gaps} onClick={run}>{busy ? <Loader2 size={16} className="spin" /> : <Play size={16} />} {Object.keys(cfg.scen?.valves || {}).length ? t.aqRunScen : t.aqRun}</button>
        {busy && <button className="btn btn-outline" data-testid="aq-cancel-btn" onClick={cancelSim}><Square size={14} /> {t.aqCancel}</button>}
      </div>
      <div className="row" data-testid="aq-leaktest">
        <button className="btn btn-outline btn-sm" data-testid="aq-leaktest-net-btn" disabled={busy} onClick={() => leakRun(false)}>💧 {t.aqLeakNetBtn}</button>
        <button className="btn btn-outline btn-sm" data-testid="aq-leaktest-only-btn" disabled={busy} onClick={() => leakRun(true)}>💧 {t.aqLeakOnlyBtn}</button>
      </div>
      <Result t={t} res={s.res} project={project} cfg={cfg} setCfg={setCfg} updateProject={updateProject} />
    </>
  );
}

// only the tools of the network in the project: sewer-only -> sewer tab, water-only -> drinking water tabs, empty -> all
const WATER_T = ['sim', 'leak', 'cal', 'lab', 'age', 'step', 'temp', 'ref'];
const isSewP = (p) => p.type === 'sewer' || p.med === 'sewer' || p.med === 'rain';
// branch split: AquaFlow = drinking water only, Stormwater Management = sewer / heavy rain (SWMM)
export const tabsFor = (project, mode) => {
  if (mode === 'storm') return ['sew', 'demo'];
  if (mode === 'aqua') return [...WATER_T, 'demo'];
  const P = project?.pipes || [], sew = P.some(isSewP), wat = P.some((p) => !isSewP(p));
  return [...(wat || !sew ? WATER_T : []), ...(sew || !wat ? ['sew'] : []), 'demo'];
};

// AquaFlow panel: hydraulic simulation, step test, temperature / stagnation
export default function AquaPanel({ t, project, updateProject }) {
  const [tab, setTab] = useState(null);
  const s = useAqua(), mode = useMode(), storm = mode === 'storm';
  const pRef = useRef(project), tRef = useRef(tab);
  pRef.current = project; tRef.current = tab;
  useRiverDyke(t, updateProject);
  // one-click changes recalculate an existing result also while the panel (sim tab) is closed
  useEffect(() => {
    const f = async () => { if (tRef.current === 'sim' || !aquaState().res || !pRef.current) return; try { const res = await simulate(pRef.current, pRef.current.aqua || {}); if (res.error !== 'cancel') setAqua({ res, on: !res.error }); } catch { /* keep old result */ } };
    window.addEventListener('giss-aqua-rerun', f); return () => window.removeEventListener('giss-aqua-rerun', f);
  }, []);
  useEffect(() => { const f = (e) => setTab(e.detail || 'sim'); window.addEventListener('giss-aqua-open', f); return () => window.removeEventListener('giss-aqua-open', f); }, []);
  useEffect(() => { const f = () => setTab(null); window.addEventListener('giss-aqua-hide', f); return () => window.removeEventListener('giss-aqua-hide', f); }, []);
  if (!tab || !project) return null;
  const tabs = tabsFor(project, mode), cur = tabs.includes(tab) ? tab : tabs[0], sewIn = mode === 'aqua' && (project.pipes || []).some(isSewP);
  const cfg = project.aqua || {};
  const setCfg = (p) => updateProject((pr) => ({ ...pr, aqua: { ...(pr.aqua || {}), ...p } }));
  const props = { t, project, updateProject, cfg, setCfg };
  return createPortal(
    <div className="aq-panel" data-testid="aqua-panel">
      <div className="sh-head"><b data-testid="aq-panel-title">{storm ? `🌧️ ${t.swTitle}` : '🌊 AquaFlow'}</b>
        {!storm && <button className="icon-btn" data-testid="aq-3d-btn" title={t.aq3d} disabled={!s.res || s.res.error} onClick={() => window.dispatchEvent(new Event('giss-aqua-3d'))}><Mountain size={18} /></button>}
        <button className="icon-btn" data-testid="aq-manual-btn" title={storm ? t.swManual : 'AquaFlow-Handbuch'} onClick={() => openHelp(storm ? 'sw:' : 'aq:')}><BookOpen size={18} /></button>
        <button className="icon-btn" data-testid={storm ? 'sw-stormy-btn' : 'aq-dripx-btn'} title={storm ? 'Stormy' : 'DripX'} onClick={() => window.dispatchEvent(new Event('giss-agent-open'))}><Bot size={18} /></button>
        <button className="icon-btn" data-testid="aq-close-btn" onClick={() => setTab(null)}><X size={18} /></button>
      </div>
      <div className="aq-tabs">
        {tabs.map((k) => <button key={k} className={`chip ${cur === k ? 'on' : ''}`} data-testid={`aq-tab-${k}`} onClick={() => { setTab(k); const m = { sim: 'p', temp: 't', age: 'age' }[k]; if (m && (m === 'p' ? s.res && !s.res.error : m === 't' ? s.th : s.th || s.eps)) setAqua({ mode: m }); }}>{t['aqTab_' + k]}</button>)}
      </div>
      {((s.res && !s.res.error) || s.eps) && <AquaLegend t={t} />}
      <div className="aq-body">
        {sewIn && <p className="aq-note" data-testid="aq-sewer-moved">🌧️ {t.swMoved} <button className="chip" data-testid="aq-to-storm-btn" onClick={() => { applyProfiles(['storm']); setTab('sew'); }}>{t.swOpen}</button></p>}
        {cur === 'sim' && <SimTab {...props} />}
        {cur === 'leak' && <AquaLeaks {...props} />}
        {cur === 'sew' && <SewTab {...props} />}
        {cur === 'step' && <AquaStep {...props} />}
        {cur === 'temp' && <AquaTemp {...props} />}
        {cur === 'cal' && <AquaCal {...props} />}
        {cur === 'lab' && <AquaLab {...props} />}
        {cur === 'age' && <AquaAge {...props} />}
        {cur === 'ref' && <AquaRef t={t} />}
        {cur === 'demo' && <AquaDemo t={t} storm={storm} />}
      </div>
    </div>,
    document.body,
  );
}
